import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  alpha,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InventoryIcon from "@mui/icons-material/Inventory";
import StorageIcon from "@mui/icons-material/Storage";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";

const emptyPlan = {
  name: "",
  description: "",
  price: 0,
  duration_days: 30,
  max_storage_gb: 10,
  max_downloads: 100,
  status: "active",
};

const ManagePlans = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();
  const [plans, setPlans] = useState([]);
  const [dialog, setDialog] = useState({ open: false, plan: null });
  const [form, setForm] = useState(emptyPlan);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    const res = await hitAxios({
      path: "/api/plans/get-all",
      showSnackbar: false,
    });
    if (res?.data?.success) setPlans(res.data.data);
  };

  const openDialog = (plan = null) => {
    setForm(plan ? { ...plan } : emptyPlan);
    setDialog({ open: true, plan });
  };

  const savePlan = async () => {
    await hitAxios({
      path: form.id ? "/api/plans/update" : "/api/plans/create",
      post: true,
      admin: true,
      obj: form,
    });
    setDialog({ open: false, plan: null });
    loadPlans();
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    await hitAxios({
      path: "/api/plans/delete",
      post: true,
      admin: true,
      obj: { id },
    });
    loadPlans();
  };

  const planColors = [
    "#6366F1",
    "#10B981",
    "#F59E0B",
    "#EF4444",
    "#8B5CF6",
    "#3B82F6",
  ];

  return (
    <Box>
      <PageHeader
        title={lang.managePlans || "Manage Plans"}
        subtitle="Create and manage subscription plans"
        icon={InventoryIcon}
        primaryAction={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog()}
            sx={{ borderRadius: 2 }}
          >
            {lang.addPlan || "Add Plan"}
          </Button>
        }
      />

      {/* ✅ MUI v6+: container Grid with size prop on children */}
      <Grid container spacing={3}>
        {plans.map((plan, idx) => {
          const color = planColors[idx % planColors.length];
          return (
            // ✅ Add `display="flex"` so the Grid cell stretches full height
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={plan.id} display="flex">
              <Card
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  width: "100%", // ✅ fill the full Grid cell width
                  display: "flex", // ✅ make Card a flex container
                  flexDirection: "column", // ✅ stack children vertically
                  border: `1px solid ${alpha(color, 0.2)}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 16px 40px ${alpha(color, 0.2)}`,
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.5)})`,
                  },
                }}
              >
                {/* ✅ CardContent grows to fill remaining space */}
                <CardContent
                  sx={{
                    p: 3,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {plan.name}
                      </Typography>
                      <Chip
                        label={plan.status}
                        size="small"
                        color={plan.status === "active" ? "success" : "default"}
                        sx={{ mt: 0.5, height: 20, fontSize: "0.65rem" }}
                      />
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => openDialog(plan)}
                        sx={{ "&:hover": { bgcolor: alpha(color, 0.1) } }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => deletePlan(plan.id)}
                        sx={{
                          "&:hover": {
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                          },
                        }}
                      >
                        <DeleteIcon
                          sx={{ fontSize: 16, color: "error.main" }}
                        />
                      </IconButton>
                    </Stack>
                  </Stack>

                  <Box sx={{ mb: 2.5 }}>
                    <Typography
                      variant="h3"
                      fontWeight={800}
                      sx={{ color, letterSpacing: "-0.04em", lineHeight: 1 }}
                    >
                      ${plan.price}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      per period
                    </Typography>
                  </Box>

                  {plan.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, fontSize: "0.8rem" }}
                    >
                      {plan.description}
                    </Typography>
                  )}

                  {/* ✅ Push features to the bottom always */}
                  <Stack spacing={1.2} sx={{ mt: "auto" }}>
                    {[
                      {
                        icon: <StorageIcon sx={{ fontSize: 14 }} />,
                        label: `${plan.max_storage_gb} GB Storage`,
                      },
                      {
                        icon: <CloudDownloadIcon sx={{ fontSize: 14 }} />,
                        label: `${plan.max_downloads} Downloads`,
                      },
                      {
                        icon: <CalendarTodayIcon sx={{ fontSize: 14 }} />,
                        label: `${plan.duration_days} Days`,
                      },
                    ].map((feat) => (
                      <Stack
                        key={feat.label}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Box sx={{ color, opacity: 0.8 }}>{feat.icon}</Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          {feat.label}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, plan: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>
          {form.id ? "Edit Plan" : "Create Plan"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              fullWidth
              size="small"
              label="Plan Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              label="Description"
              multiline
              rows={2}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />

            {/* ✅ MUI v6+ Grid inside Dialog — size prop, no item */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Price (USD)"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Duration (days)"
                  value={form.duration_days}
                  onChange={(e) =>
                    setForm({ ...form, duration_days: e.target.value })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Max Storage (GB)"
                  value={form.max_storage_gb}
                  onChange={(e) =>
                    setForm({ ...form, max_storage_gb: e.target.value })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Max Downloads"
                  value={form.max_downloads}
                  onChange={(e) =>
                    setForm({ ...form, max_downloads: e.target.value })
                  }
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={form.status === "active"}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      status: e.target.checked ? "active" : "inactive",
                    })
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDialog({ open: false, plan: null })}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={savePlan}
            sx={{ borderRadius: 2 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManagePlans;
